// Shared image-insert path. Used by the toolbar Image button, clipboard paste,
// and drag-and-drop in PlateEditor.tsx. Performs MIME magic-byte validation
// (D1), generates a placeholder id, inserts the image with the base64 URL
// + transient `_uploading` flag, then asynchronously uploads to S3 (D2).
// When the upload returns a docId, the node is updated in place via setNodes.

import { registerPendingUpload, uploadSingleImage } from '../../services/imageUploadService';
import { sniffImageMime } from './imageMimeSniff';

const MAX_BYTES = 8 * 1024 * 1024;
const UPLOAD_DEBOUNCE_MS = 500;

type InsertError = 'invalid-mime' | 'too-large' | 'read-failed';

export interface InsertResult {
    error?: InsertError;
    placeholderId?: string;
}

function readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

function genPlaceholderId(): string {
    return `up_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function findPathByPlaceholder(editor: any, placeholderId: string): number[] | null {
    const root = editor?.children;
    if (!Array.isArray(root)) return null;
    const walk = (nodes: any[], parentPath: number[]): number[] | null => {
        for (let i = 0; i < nodes.length; i++) {
            const n = nodes[i];
            if (!n || typeof n !== 'object') continue;
            const path = [...parentPath, i];
            if (n.type === 'image' && n._uploading === placeholderId) return path;
            if (Array.isArray(n.children)) {
                const found = walk(n.children, path);
                if (found) return found;
            }
        }
        return null;
    };
    return walk(root, []);
}

async function startLazyUpload(editor: any, placeholderId: string, dataUrl: string, source: string): Promise<void> {
    // Debounce so a burst of inserts (e.g. paste of HTML with multiple imgs)
    // doesn't fire upload-all at once before the editor settles.
    await new Promise((r) => setTimeout(r, UPLOAD_DEBOUNCE_MS));
    try {
        const docId = await uploadSingleImage(dataUrl, source);
        const path = findPathByPlaceholder(editor, placeholderId);
        if (!path) return;
        try {
            editor?.tf?.setNodes?.({ url: docId, _uploading: undefined, _uploadError: undefined }, { at: path });
        } catch {
            /* defensive: editor may have unmounted */
        }
    } catch (err) {
        console.error('[handleImageInsert] upload failed', err);
        const path = findPathByPlaceholder(editor, placeholderId);
        if (!path) return;
        try {
            editor?.tf?.setNodes?.({ _uploading: undefined, _uploadError: true }, { at: path });
        } catch {
            /* defensive */
        }
    }
}

export async function handleImageInsert(file: File, editor: any, source = 'one-pager'): Promise<InsertResult> {
    if (!file) return { error: 'invalid-mime' };
    if (file.size > MAX_BYTES) return { error: 'too-large' };

    const sniffedMime = await sniffImageMime(file);
    if (!sniffedMime) return { error: 'invalid-mime' };

    let dataUrl: string;
    try {
        dataUrl = await readAsDataUrl(file);
    } catch (err) {
        console.error('[handleImageInsert] failed to read file', err);
        return { error: 'read-failed' };
    }

    const placeholderId = genPlaceholderId();
    try {
        editor?.tf?.insertNodes?.({
            type: 'image',
            url: dataUrl,
            _uploading: placeholderId,
            children: [{ text: '' }],
        });
    } catch (err) {
        console.error('[handleImageInsert] insertNodes failed', err);
        return { error: 'read-failed' };
    }

    const uploadPromise = startLazyUpload(editor, placeholderId, dataUrl, source);
    registerPendingUpload(placeholderId, uploadPromise);

    return { placeholderId };
}
